import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { THEME_TOKEN, Theme } from '../types/theme.types';
import { componentStylesByTheme } from '../utils/theme.utils';
import {
  scdPickerStylesSAG,
  scdPickerStylesSHS,
} from './scd-picker-extended.styles';
import { DialogService } from '../dialog/dialog.service';
import {
  ScdDialogConfig,
  ScdPickerDialogComponent,
} from './scd-picker-dialog.component';
import { ScdPickerBaseValue } from '../scd-picker/scd-picker.component';
import {
  ScdPickerExtendedBaseValue,
  ScdPickerSearchQuery,
  ScdPickerTranslations,
} from './scd-picker-extended.types';

@Component({
  selector: 'sag-ui-scd-picker-extended',
  templateUrl: './scd-picker-extended.component.html',
  styleUrls: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScdPickerExtendedComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ScdPickerExtendedComponent),
      multi: true,
    },
  ],
})
export class ScdPickerExtendedComponent<T extends ScdPickerExtendedBaseValue>
  implements ControlValueAccessor, Validator
{
  @Input() id = '';
  /**
   * The placeholder of the ScdPicker.
   */
  @Input()
  placeholder = '';

  /*
   * Whether the ScdPicker is required.
   */
  @Input()
  required = false;

  /**
   * Whether the ScdPicker is disabled.
   */
  @Input()
  disabled = false;

  /**
   * Whether the ScdPicker is read-only.
   */
  @Input()
  readOnly = false;

  /**
   * The label of the ScdPicker.
   */
  @Input()
  label = '';

  /**
   * Whether the SCD Picker is in an error state.
   */
  @Input() invalid = false;

  /**
   * The error message to display.
   */
  @Input() error = '';

  @Input() hint?: string;

  @Input() translations: ScdPickerTranslations = {
    dialog: {
      title: 'Search User',
      subtitle: 'Please enter the user details to search for',
      labels: {
        firstName: 'First Name',
        lastName: 'Last Name',
      },
      table: {
        header: {
          name: 'Name',
          gid: 'GID',
          department: 'Department',
          select: 'Select',
        },
        select: 'Select',
      },
      noResults: 'No results found',
      cancel: 'Cancel',
      errors: {
        firstNameRequired: 'First name is required',
        lastNameRequired: 'Last name is required',
      },
    },
  };

  /**
   * Whether the switch is checked.
   */
  @Input()
  value: T | undefined;

  @Input({ required: true })
  searchFn!: (data: ScdPickerSearchQuery) => Promise<T[]>;

  @ViewChild('input') input!: ElementRef;

  @HostListener('document:mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    const clickedInside = this.el.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.isOpen = false;
    }
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const focusedInside = this.el.nativeElement.contains(event.relatedTarget);
    if (!focusedInside) {
      this.isOpen = false;
    }
  }

  touched = false;
  protected selected: T | undefined;
  protected isOpen = false;

  constructor(
    @Inject(THEME_TOKEN) private readonly theme: Theme,
    private el: ElementRef,
    private dialogService: DialogService,
  ) {}

  protected openDialog() {
    const config = {
      data: { searchFn: this.searchFn, translations: this.translations },
    };

    const dialogRef = this.dialogService.open<
      ScdPickerBaseValue,
      ScdDialogConfig<T>
    >(ScdPickerDialogComponent, config);

    dialogRef.afterClosed.subscribe((result) => {
      if (result) {
        this.writeValue(result);
        this.selected = result as T;
        this.markAsTouched();
      }
    });
  }

  protected get popoverClasses() {
    const popoverStyles = componentStylesByTheme(
      this.theme,
      scdPickerStylesSAG,
      scdPickerStylesSHS,
    );

    return popoverStyles();
  }

  protected get displayValue(): string {
    if (!this.selected) return '';
    return `${this.selected?.firstName} ${this.selected?.lastName}`;
  }

  protected onFocus() {
    if (this.disabled || this.readOnly) return;

    this.isOpen = true;
  }

  protected onBlur() {
    setTimeout(() => {
      this.markAsTouched();
    }, 100);
  }

  // eslint-disable-next-line
  onChange = (_value: any) => {};
  // eslint-disable-next-line
  onTouch = () => {};

  // eslint-disable-next-line
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(val: any) {
    this.value = val;
    this.selected = val;
    this.onChange(this.value);
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouch();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  // eslint-disable-next-line
  validate(control: AbstractControl<any, any>): ValidationErrors | null {
    return this.required && !this.selected ? { required: true } : null;
  }

  blockChange(event: Event) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (event.target && (event.target as any)['value']) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event.target as any)['value'] = this.displayValue;
    }
  }
}
